import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Plus } from "lucide-react";
import ChatThread from "@/components/business/ChatThread";
import PageHeader from "@/components/business/PageHeader";

const SUGGESTIONS = [
  "What's the difference between old and new tax regime for AY 2025-26?",
  "Can I claim HRA if I'm paying rent to my parents?",
  "What deductions are allowed under the new regime?",
  "I sold mutual funds in March 2025 — how is the LTCG taxed?",
  "I'm a freelancer earning ₹18 lakh — which ITR form should I file?",
];

export default function AIChat() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const loadSessions = () => api.get("/ai/sessions").then(({ data }) => setSessions(data));

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (sessionId) api.get(`/ai/sessions/${sessionId}`).then(({ data }) => setMessages(data.messages || []));
    else setMessages([]);
  }, [sessionId]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat-sync", { message: msg, session_id: sessionId });
      if (!sessionId) {
        setSessionId(data.session_id);
        loadSessions();
      }
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: data.reply };
        return c;
      });
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond. Please try again." };
        return c;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <PageHeader>
          <PageHeader.Title>AI Tax Assistant</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Conversational tax expert — trained on the entire Indian Income Tax Act, latest CBDT circulars and Budget 2024.</p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          <Card className="p-4 border-border h-fit lg:sticky lg:top-20">
            <Button className="w-full" onClick={() => { setSessionId(null); setMessages([]); }} data-testid="ai-new-chat">
              <Plus className="h-4 w-4 mr-2" /> New chat
            </Button>
            <div className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">Recent</div>
            <div className="mt-2 space-y-1 max-h-[60vh] overflow-y-auto">
              {sessions.length === 0 && <div className="text-xs text-muted-foreground">Your conversations appear here.</div>}
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => setSessionId(s.session_id)} data-testid={`ai-session-${s.session_id.slice(0, 8)}`} className={`w-full text-left rounded-md px-3 py-2 text-xs leading-snug ${sessionId === s.session_id ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"}`}>
                  <div className="truncate font-medium">{s.preview}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">{s.count} messages</div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="border-border flex flex-col h-[72vh] overflow-hidden">
            <ChatThread
              messages={messages}
              onSend={send}
              sending={sending}
              input={input}
              onInputChange={setInput}
              placeholder="Ask anything about Indian Income Tax…"
              listClassName="p-6 space-y-4"
              bubbleClassName="px-4 py-2.5"
              formTestId="ai-page-form"
              inputTestId="ai-page-input"
              sendTestId="ai-page-send"
              emptyState={
                <div className="max-w-2xl mx-auto text-center pt-10">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/8 text-primary">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <h2 className="mt-5 font-display text-2xl font-semibold">Hi, I'm Ritika.</h2>
                  <p className="mt-2 text-sm text-muted-foreground">Ask me anything about deductions, regime selection, ITR forms, notices, or your filing.</p>
                  <div className="mt-8 grid sm:grid-cols-2 gap-3">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} className="text-left rounded-md border border-border hover:border-primary p-3 text-sm transition-colors" data-testid="ai-suggestion">{s}</button>
                    ))}
                  </div>
                </div>
              }
            />
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
